// about.js
//
// Structured page content for the About_Page. Company overview, values,
// credibility content, the closing CTA, imagery references, and SEO metadata
// live here rather than in component templates so copy changes need no redesign
// (Requirements 11.2, 10.1, 5.1–5.4).
//
// `AboutPage.vue` imports this module.

import overviewImage from 'assets/img/parallax/aerial.jpg'

/**
 * The About_Page content model.
 *
 * @typedef {Object} AboutContent
 * @property {Object} overview   - company overview / mission (Requirement 5.1)
 * @property {Object} values     - values / differentiators (Requirement 5.2)
 * @property {Object} credibility - experience / accreditations / milestones (5.3)
 * @property {Object} closingCta - CTA to Contact (Requirement 5.4)
 * @property {import('./seo').PageSeo} seo - per-page SEO metadata
 */

export const about = {
  overview: {
    heading: 'About ThinkSpatial',
    image: {
      src: overviewImage,
      alt: 'Aerial view of a surveyed project site'
    },
    mission:
      'ThinkSpatial is a spatial information specialist working across surveying, GIS, engineering, and spatial technology. Our mission is to capture, model, and deliver spatial data our clients can act on with confidence.',
    // A paragraph is either a plain string, or an array of segments when part of
    // it needs to be a link. A segment with an `href` renders as an anchor; one
    // without renders as text. Segments are interpolated individually, so the
    // copy is still escaped by Vue — no raw HTML in content, and no `v-html` in
    // the page. Keep any needed spacing inside the segment text.
    body: [
      'We partner with asset owners, government and infrastructure bodies, and engineering and construction teams to solve spatial problems end to end — from field capture through to the software that puts data to work.',
      [
        { text: 'Our points of difference include project survey management, spatial data management, subsurface utilities capture and presentation, automated survey monitoring, UAV aerial surveying, laser scanning, 3D CAD/BIM modelling, the installation of GNSS base stations, and our own web-mapping platform, ' },
        { text: 'Civillo', href: 'https://www.civillo.com' },
        { text: '.' }
      ],
      'Whether the task calls for a licensed surveyor on the ground, a UAV in the air, or a custom platform to manage the result, we bring the right mix of people and technology to the project.'
    ]
  },

  // Team composition and delivery capacity, from the capability statement.
  team: {
    heading: 'Our team',
    intro:
      'ThinkSpatial brings together a professional and experienced team across the field, the back office, and the software that ties them together.',
    roles: [
      'Survey managers',
      'Spatial data managers',
      'Surveyors',
      'Survey assistants',
      'Software developers & programmers',
      'Support staff'
    ],
    capacity:
      'Our survey teams are supported by comprehensive back-office expertise and a wide range of state-of-the-art equipment. We resource everything from small, part-time works through to large-scale infrastructure projects requiring 6–15 surveyors full-time, including night shift and weekend shifts, scaling up for occupations.'
  },

  values: {
    heading: 'What sets us apart',
    items: [
      {
        title: 'Spatial expertise, end to end',
        body:
          'Surveying, asset capture, monitoring, UAV, and software under one roof, so data flows cleanly from the field to your systems.'
      },
      {
        title: 'Technology that fits the job',
        body:
          'We adopt modern spatial technology where it adds value and pair it with proven survey practice — never novelty for its own sake.'
      },
      {
        title: 'Reliable, accurate, accountable',
        body:
          'Rigorous quality control stands behind every dataset we deliver.'
      }
    ]
  },

  credibility: {
    heading: 'A track record on major programs',
    intro:
      'Our work spans landmark infrastructure and asset programs across Australia.',
    milestones: [
      {
        title: 'Tindal RAAF Base — Katherine, NT',
        body:
          'Supplying engineering survey services for multiple contractors in Katherine at the Tindal RAAF Base.'
      },
      {
        title: 'North West Programme Alliance — Melbourne, Vic',
        body:
          'Supplied survey and spatial services on multiple John Holland run NWPA projects for nearly 10 years.'
      },
      {
        title: 'Coomera Connector — Gold Coast, Qld',
        body:
          'On-site engineering surveying and data management for the ongoing works on stage 1 of the Coomera Connector project.'
      }
    ]
  },

  closingCta: {
    heading: 'Let’s work together',
    body:
      'Talk to our team about how ThinkSpatial can support your next project.',
    cta: { label: 'Contact us', to: '/contact' }
  },

  /** @type {import('./seo').PageSeo} */
  seo: {
    title: 'About Us — ThinkSpatial',
    description:
      'Learn about ThinkSpatial, a spatial information specialist delivering surveying, GIS, UAV, monitoring, and spatial software across Australia.'
  }
}

export default about
